import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, ComposedChart,
} from "recharts";
import { Settings as SettingsIcon, AlertTriangle, Boxes, TrendingUp, Layers, X, Wrench } from "lucide-react";
import { Link } from "react-router-dom";

const fmtKg = (v) => `${(v ?? 0).toLocaleString("en-ZA", { maximumFractionDigits: 1 })} kg`;
const fmtPct = (v, d = 1) => v == null ? "—" : `${(v).toFixed(d)}%`;

const WIDGETS = [
  { id: "mtd-kg",            label: "MTD volume vs target",        icon: TrendingUp,     default: true },
  { id: "maintenance",       label: "Maintenance attention",       icon: Wrench,         default: true },
  { id: "monthly-vol",       label: "12-month volume + targets",   icon: Layers,         default: true },
  { id: "inventory",         label: "Green inventory forecast",    icon: Boxes,          default: true },
  { id: "blend-margin",      label: "Blend gross-margin trend",    icon: TrendingUp,     default: true },
  { id: "summary-kpis",      label: "Roastery KPIs (legacy)",      icon: AlertTriangle,  default: false },
];

const STORAGE_KEY = "dashboard.widgets.v1";

function loadEnabled() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) return JSON.parse(v);
  } catch { /* fall through */ }
  return WIDGETS.filter((w) => w.default).map((w) => w.id);
}
function saveEnabled(ids) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

export default function Dashboard() {
  const [enabled, setEnabled] = useState(loadEnabled());
  const [showCustomize, setShowCustomize] = useState(false);

  const toggle = (id) => {
    const next = enabled.includes(id) ? enabled.filter((x) => x !== id) : [...enabled, id];
    setEnabled(next);
    saveEnabled(next);
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Overview"
        title="Roastery Dashboard"
        description="Pinned widgets for everything you care about. Click Customize to choose what's shown."
        testId="dashboard-header"
        actions={
          <button onClick={() => setShowCustomize(true)} data-testid="dashboard-customize"
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm hover:bg-secondary">
            <SettingsIcon className="w-4 h-4" /> Customize
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {enabled.includes("mtd-kg") && <WidgetMtdKg />}
        {enabled.includes("maintenance") && <WidgetMaintenanceOverdue />}
        {enabled.includes("inventory") && <WidgetInventoryForecast />}
        {enabled.includes("monthly-vol") && <WidgetMonthlyVolume />}
        {enabled.includes("blend-margin") && <WidgetBlendMargin />}
        {enabled.includes("summary-kpis") && <WidgetLegacyKpis />}
      </div>

      {enabled.length === 0 && (
        <div className="bg-card border border-border rounded p-12 text-center text-muted-foreground" data-testid="dashboard-empty">
          <p className="mb-3">No widgets pinned. Click <strong>Customize</strong> above to add some.</p>
        </div>
      )}

      {showCustomize && (
        <CustomizeModal enabled={enabled} onToggle={toggle} onClose={() => setShowCustomize(false)} />
      )}
    </div>
  );
}

function CustomizeModal({ enabled, onToggle, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="dashboard-customize-modal">
      <div className="bg-card border border-border rounded shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-display text-lg font-bold">Customize dashboard</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-2">
          <p className="text-xs text-muted-foreground mb-3">
            Pick which widgets to show. Your selection is saved to this browser.
          </p>
          {WIDGETS.map((w) => (
            <label key={w.id} className="flex items-center gap-3 p-3 border border-border rounded cursor-pointer hover:bg-secondary"
                   data-testid={`widget-toggle-${w.id}`}>
              <input type="checkbox" checked={enabled.includes(w.id)}
                onChange={() => onToggle(w.id)}
                className="accent-[hsl(20,39%,39%)]" />
              <w.icon className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{w.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Widgets ----------------------------------------------------------------
function WidgetCard({ title, subtitle, children, testId }) {
  return (
    <section className="bg-card border border-border rounded p-5" data-testid={testId}>
      <div className="mb-3">
        <h3 className="font-display text-base font-bold">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function useWidget(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(path).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || e.message));
  }, [path]);
  return { data, error };
}

function WidgetMtdKg() {
  const { data, error } = useWidget("/dashboard/widget/mtd-kg");
  if (error) return <WidgetCard title="MTD volume vs target" testId="widget-mtd-kg"><p className="text-xs text-destructive">{error}</p></WidgetCard>;
  if (!data) return <WidgetCard title="MTD volume vs target" testId="widget-mtd-kg"><p className="text-xs text-muted-foreground">Loading…</p></WidgetCard>;
  const tonePct = data.target_kg <= 0 ? "text-muted-foreground" :
                    data.on_track ? "text-[hsl(132,18%,36%)]" : "text-destructive";
  return (
    <WidgetCard title="Month-to-date volume" subtitle={`Pace ${data.pace_pct_of_month}% through ${data.year}-${String(data.month).padStart(2, "0")}`} testId="widget-mtd-kg">
      <div className="flex items-end gap-4 mb-3">
        <div>
          <p className="label-tiny">Sold</p>
          <p className="font-display text-3xl font-bold">{fmtKg(data.kg_actual)}</p>
        </div>
        <div className="text-muted-foreground text-2xl">/</div>
        <div>
          <p className="label-tiny">Target</p>
          <p className="font-display text-2xl">{data.target_kg > 0 ? fmtKg(data.target_kg) : "— not set —"}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="label-tiny">Of target</p>
          <p className={`font-display text-2xl font-bold ${tonePct}`}>{fmtPct(data.pct_of_target)}</p>
        </div>
      </div>
      {data.target_kg > 0 && (
        <div className="h-2 bg-border rounded overflow-hidden">
          <div className={`h-full ${data.on_track ? "bg-[hsl(132,18%,36%)]" : "bg-[hsl(31,65%,53%)]"}`}
               style={{ width: `${Math.min(100, data.pct_of_target ?? 0)}%` }} />
        </div>
      )}
    </WidgetCard>
  );
}

function WidgetMonthlyVolume() {
  const { data, error } = useWidget("/dashboard/widget/monthly-volume?months=12");
  if (error) return <WidgetCard title="12-month volume" testId="widget-monthly-vol"><p className="text-xs text-destructive">{error}</p></WidgetCard>;
  if (!data) return <WidgetCard title="12-month volume" testId="widget-monthly-vol"><p className="text-xs text-muted-foreground">Loading…</p></WidgetCard>;
  return (
    <WidgetCard title="Monthly volume vs target" subtitle="Last 12 months · kg sold (bar) vs target (line)" testId="widget-monthly-vol">
      <div className="h-64">
        <ResponsiveContainer>
          <ComposedChart data={data.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 21% 87%)" />
            <XAxis dataKey="x" tick={{ fontSize: 9, fill: "hsl(25 8% 42%)" }} />
            <YAxis tick={{ fontSize: 9, fill: "hsl(25 8% 42%)" }} />
            <Tooltip contentStyle={{ background: "white", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 11 }}
              formatter={(v) => `${Number(v).toFixed(1)} kg`} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="kg_actual" name="Sold" fill="#8C583E" />
            <Line dataKey="kg_target" name="Target" stroke="#5C7C5A" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </WidgetCard>
  );
}

function WidgetInventoryForecast() {
  const { data, error } = useWidget("/dashboard/widget/inventory-forecast");
  if (error) return <WidgetCard title="Green inventory forecast" testId="widget-inventory"><p className="text-xs text-destructive">{error}</p></WidgetCard>;
  if (!data) return <WidgetCard title="Green inventory forecast" testId="widget-inventory"><p className="text-xs text-muted-foreground">Loading…</p></WidgetCard>;
  return (
    <WidgetCard title="Green inventory forecast" subtitle="Days remaining at last-90-day burn rate" testId="widget-inventory">
      {data.origins.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No active origins.</p>}
      <div className="space-y-1.5">
        {data.origins.slice(0, 8).map((o) => {
          const days = o.days_remaining;
          const tone = days == null ? "bg-muted text-muted-foreground" :
                        days < 14 ? "bg-destructive/15 text-destructive" :
                        days < 30 ? "bg-[hsl(31,65%,53%)]/15 text-[hsl(31,65%,53%)]" :
                        "bg-[hsl(132,18%,36%)]/15 text-[hsl(132,18%,36%)]";
          return (
            <div key={o.origin_id} className="flex items-center gap-3 text-sm" data-testid={`inv-row-${o.origin_id}`}>
              <span className="flex-1 truncate">{o.origin_name}</span>
              <span className="text-xs text-muted-foreground font-mono">{fmtKg(o.on_hand_kg)}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${tone}`}>
                {days == null ? "—" : `${days.toFixed(0)} days`}
              </span>
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
}

function WidgetBlendMargin() {
  const { data, error } = useWidget("/dashboard/widget/blend-margin?months=6");
  if (error) return <WidgetCard title="Blend margin trend" testId="widget-blend-margin"><p className="text-xs text-destructive">{error}</p></WidgetCard>;
  if (!data) return <WidgetCard title="Blend margin trend" testId="widget-blend-margin"><p className="text-xs text-muted-foreground">Loading…</p></WidgetCard>;
  // Pivot: rows of {x, blend1: gm_pct, blend2: gm_pct, ...}
  const blendNames = Object.keys(data.blends);
  const series = data.months.map((x) => {
    const row = { x };
    blendNames.forEach((b) => {
      const pt = data.blends[b].find((p) => p.x === x);
      row[b] = pt?.gm_pct ?? null;
    });
    return row;
  });
  const palette = ["#8C583E", "#5C7C5A", "#E97B1B", "#3F4D6C", "#A65528", "#7B5B2C"];
  return (
    <WidgetCard title="Blend gross-margin trend"
                subtitle={blendNames.length === 0 ? "Map items to blends to see margin curves" : "Last 6 months · GM % per blend"}
                testId="widget-blend-margin">
      {blendNames.length === 0 ? (
        <p className="text-xs text-muted-foreground py-8 text-center">
          No blend-mapped sales found. Map items to blends in <em>Item Mapping</em> to populate this chart.
        </p>
      ) : (
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 21% 87%)" />
              <XAxis dataKey="x" tick={{ fontSize: 9, fill: "hsl(25 8% 42%)" }} />
              <YAxis tick={{ fontSize: 9, fill: "hsl(25 8% 42%)" }} unit="%" />
              <Tooltip contentStyle={{ background: "white", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 11 }}
                formatter={(v) => v == null ? "—" : `${Number(v).toFixed(1)}%`} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {blendNames.map((b, i) => (
                <Line key={b} dataKey={b} stroke={palette[i % palette.length]}
                      strokeWidth={2} dot={{ r: 3 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </WidgetCard>
  );
}

// Overdue-maintenance card — completes the tablet → desktop feedback loop.
// Shows a compact "N need attention" tile with the worst-offending (task,
// machine) pairs, colour-coded by status. Clicking a row deep-links to the
// Maintenance cockpit.
function WidgetMaintenanceOverdue() {
  const { data, error } = useWidget("/dashboard/widget/maintenance-overdue");
  if (error) return (
    <WidgetCard title="Maintenance attention" testId="widget-maintenance-overdue">
      <p className="text-xs text-destructive">{error}</p>
    </WidgetCard>
  );
  if (!data) return (
    <WidgetCard title="Maintenance attention" testId="widget-maintenance-overdue">
      <p className="text-xs text-muted-foreground">Loading…</p>
    </WidgetCard>
  );

  const { counts, total, items } = data;
  const empty = total === 0;
  const chipCls = {
    overdue: "bg-destructive/15 text-destructive border-destructive/30",
    due:     "bg-orange-100 text-orange-800 border-orange-300",
    never:   "bg-muted text-muted-foreground border-border",
  };
  const chipLabel = { overdue: "Overdue", due: "Due", never: "Not yet" };

  return (
    <WidgetCard
      title="Maintenance attention"
      subtitle={empty ? "All active tasks are on track" : "Tasks past their due window across all machines"}
      testId="widget-maintenance-overdue"
    >
      {empty ? (
        <div className="py-6 text-center" data-testid="maintenance-overdue-empty">
          <Wrench className="w-8 h-8 mx-auto text-[hsl(132,18%,36%)] mb-2 opacity-70" />
          <p className="text-sm text-muted-foreground">Nothing overdue right now.</p>
          <Link
            to="/maintenance"
            className="inline-block mt-2 text-xs text-primary hover:underline"
            data-testid="maintenance-overdue-open-cockpit"
          >
            Open maintenance cockpit →
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <SummaryTile label="Overdue" value={counts.overdue} tone="destructive" testId="maintenance-count-overdue" />
            <SummaryTile label="Due" value={counts.due} tone="warning" testId="maintenance-count-due" />
            <SummaryTile label="Never done" value={counts.never} tone="muted" testId="maintenance-count-never" />
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {items.slice(0, 8).map((it, i) => (
              <Link
                key={`${it.task_id}-${it.machine_id || "none"}-${i}`}
                to="/maintenance"
                className="flex items-center gap-3 text-sm p-1.5 rounded hover:bg-secondary/60 transition-colors"
                data-testid={`maintenance-row-${it.task_id}`}
              >
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${chipCls[it.status]}`}>
                  {chipLabel[it.status]}
                </span>
                <span className="flex-1 truncate">{it.task_name}</span>
                <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                  {it.machine_name || "—"}
                </span>
              </Link>
            ))}
          </div>
          {items.length > 8 && (
            <Link to="/maintenance" className="mt-2 block text-xs text-center text-primary hover:underline"
                  data-testid="maintenance-overdue-view-all">
              View all {items.length} in the cockpit →
            </Link>
          )}
        </>
      )}
    </WidgetCard>
  );
}

function SummaryTile({ label, value, tone, testId }) {
  const cls = {
    destructive: "border-destructive/40 text-destructive",
    warning:     "border-[hsl(31,65%,53%)]/40 text-[hsl(31,65%,53%)]",
    muted:       "border-border text-muted-foreground",
  }[tone] || "border-border";
  return (
    <div className={`border rounded p-2 text-center ${cls}`} data-testid={testId}>
      <p className="label-tiny">{label}</p>
      <p className="font-display text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function WidgetLegacyKpis() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  if (!data) return <WidgetCard title="Roastery KPIs" testId="widget-legacy"><p className="text-xs text-muted-foreground">Loading…</p></WidgetCard>;
  if (data === false) return null;
  const c = data.counts;
  const tiles = [
    ["Roasts", c.roasts.toLocaleString("en-ZA")],
    ["Origins", c.origins],
    ["Blends", c.blends],
    ["Flagged", c.flagged_roasts, c.flagged_roasts > 0],
    ["Imports", c.imports],
    ["Green lots", c.green_lots],
  ];
  return (
    <WidgetCard title="Roastery KPIs" testId="widget-legacy">
      <div className="grid grid-cols-3 gap-2 text-sm">
        {tiles.map(([label, value, accent]) => (
          <div key={label} className={`border rounded p-2 ${accent ? "border-[hsl(31,65%,53%)]" : "border-border"}`}>
            <p className="label-tiny">{label}</p>
            <p className={`font-display text-xl font-bold ${accent ? "text-[hsl(31,65%,53%)]" : ""}`}>{value}</p>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}
